import { useCallback, useEffect, useRef, useState } from "react";

export interface Pager {
  /** Token to send with the current request; `""` for the first page. */
  token: string;
  /** Zero-based index of the page being shown. */
  page: number;
  hasPrev: boolean;
  /** Advances using the `next_page_token` from the response just rendered. */
  goNext: (nextPageToken: string) => void;
  goPrev: () => void;
  reset: () => void;
}

/**
 * Cursor pagination for the admin list RPCs, which return an opaque
 * `next_page_token` and no total.
 *
 * Tokens are one-way, so "previous" is implemented by remembering the tokens
 * already visited rather than by asking the server to walk backwards. The
 * stack is dropped whenever `resetKey` changes, which is how a filter change
 * sends the operator back to page one instead of into a token that belonged to
 * a different query.
 */
export function usePager(resetKey: string): Pager {
  const [stack, setStack] = useState<string[]>([""]);
  const [page, setPage] = useState(0);
  const lastKey = useRef(resetKey);

  useEffect(() => {
    if (lastKey.current === resetKey) return;
    lastKey.current = resetKey;
    setStack([""]);
    setPage(0);
  }, [resetKey]);

  const goNext = useCallback(
    (nextPageToken: string) => {
      if (nextPageToken === "") return;
      setStack((prev) => [...prev.slice(0, page + 1), nextPageToken]);
      setPage((p) => p + 1);
    },
    [page],
  );

  const goPrev = useCallback(() => setPage((p) => Math.max(0, p - 1)), []);

  const reset = useCallback(() => {
    setStack([""]);
    setPage(0);
  }, []);

  return {
    token: stack[page] ?? "",
    page,
    hasPrev: page > 0,
    goNext,
    goPrev,
    reset,
  };
}
